import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Row } from '@sorare/core/src/components/seo/ui';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { PlayerInjuries_injury } from './__generated__/index.graphql.ts';

type Props = {
  injuries: PlayerInjuries_injury[];
};

export const PlayerInjuries = ({ injuries }: Props) => {
  return (
    <Vertical gap={1.5}>
      <Row
        style={{
          gridTemplateColumns: `1fr repeat(3, calc(4 * var(--triple-unit)))`,
        }}
      >
        <HeadlineS as="h2">
          <FormattedMessage defaultMessage="Injuries" />
        </HeadlineS>
        <LabelS as="p" color="var(--c-nd-600)" className="text-right">
          <FormattedMessage defaultMessage="From" />
        </LabelS>
        <LabelS as="p" color="var(--c-nd-600)" className="text-right">
          <FormattedMessage defaultMessage="To" />
        </LabelS>
        <LabelS as="p" color="var(--c-nd-600)" className="text-right">
          <FormattedMessage {...glossary.duration} />
        </LabelS>
      </Row>
      {injuries.map(injury => (
        <Row
          key={injury.id}
          style={{
            gridTemplateColumns: `1fr repeat(3, calc(12 * var(--unit)))`,
          }}
        >
          <LabelL as="p" bold>
            {injury.kind}
          </LabelL>
          <LabelL as="p" className="text-right">
            {injury.startDate ? (
              <FormattedDate
                value={injury.startDate}
                day="numeric"
                month="numeric"
                year="numeric"
                timeZone="UTC"
              />
            ) : (
              '-'
            )}
          </LabelL>
          <LabelL as="p" className="text-right">
            {injury.expectedEndDate ? (
              <FormattedDate
                value={injury.expectedEndDate}
                day="numeric"
                month="numeric"
                year="numeric"
                timeZone="UTC"
              />
            ) : (
              '-'
            )}
          </LabelL>
          <LabelL as="p">
            {!!injury.expectedEndDate && !!injury.startDate ? (
              <FormattedMessage
                defaultMessage="{duration} days"
                values={{
                  duration: differenceInDays(
                    injury.expectedEndDate,
                    injury.startDate
                  ),
                }}
              />
            ) : (
              '-'
            )}
          </LabelL>
        </Row>
      ))}
    </Vertical>
  );
};

PlayerInjuries.fragments = {
  anyPlayer: gql`
    fragment PlayerInjuries_injury on Injury {
      id
      startDate
      expectedEndDate
      kind
    }
  ` as TypedDocumentNode<PlayerInjuries_injury>,
};
